import * as React from 'react';
import './validationAlert.scss';
import { Alert } from 'rcd-cpp-ui/ui/alert/Alert';
import { TreeFieldUtils } from '../../../utils/treeFieldUtils';
import { getValidationErrors } from '../FieldGroup/fieldGroup';
import { FieldType } from '../../../enums/FieldType';
import { MetadataProps } from '../../../types';
import { useSelector } from '../../../utils/useSelector';
import { DataType } from '../../../enums/DataType';
import { ApprovalType } from '../../../enums/ApprovalType';
import {
  getFieldsToDisplay,
  getSchemaForm,
  skipConfigureWorkflowValidation
} from '../../../utils';
import { ShowFieldsOption } from '../../../enums/ShowFieldsOption';
import { ValidationMode } from '../../../enums/ValidationMode';
import { useActions } from '../metadataFormActions';

type VAProps = {
  impliedDataProps: MetadataProps;
};

export const ValidationAlert = ({ impliedDataProps }: VAProps) => {
  const { setAllErrorFields } = useActions();
  const pushResponse = useSelector(s => s.form?.pushResponse);
  const fieldErrors = useSelector(s => s.form?.fieldErrors);
  const shouldValidate = useSelector(s => s.form?.shouldValidate);
  const data = useSelector(s => s.form?.data);
  const validateInProgressMsg = useSelector(s => s.form?.validateInProgressMsg);
  const schema = useSelector(s => s.form?.schema);
  const schemas = useSelector(s => s.planning?.selectedWorkspace?.schemas);
  const approvalInProgress = useSelector(s => s.form?.approvalInProgress);
  const schemaForm = getSchemaForm(schema, data);
  const backingUpFor = useSelector(s => s.view?.backingUpFor) || null;
  const actionEmployeeId = useSelector(
    s => s.view?.actingAs?.props?.empCd || s.app?.user?.employeeId
  );
  const skipConfigureWorkflow = skipConfigureWorkflowValidation(data);
  let mqValidationType = null;
  let validationErrors;

  data?.approvals?.some(approval => {
    if (
      (backingUpFor == null &&
        approval?.taskAssignee?.some(
          ta => ta.props?.empCd == actionEmployeeId
        )) ||
      (approval?.taskAssignee?.some(
        ta => backingUpFor?.empCd === ta.props?.empCd
      ) &&
        backingUpFor?.roleIds.includes(approval?.roleId))
    ) {
      if (approval?.approvalStatus) {
        mqValidationType = approval?.approvalStatus;
      }
    }
  });

  const allErrorFields = new Set<string>();

  if (shouldValidate && shouldValidate != ValidationMode.None) {
    const fields = getFieldsToDisplay(
      schema,
      data,
      ShowFieldsOption.All,
      impliedDataProps
    ).filter(
      f => f?.dataType != DataType.link && f?.fieldType != FieldType.computed
    );
    fields?.forEach(field => {
      let shouldValidateField;
      if (skipConfigureWorkflow && field.key === 'Item.configureWorkflowType') {
        shouldValidateField = ValidationMode.None;
      }
      const treeSetting = TreeFieldUtils.getTreeSetting(
        schemaForm,
        field,
        schemas
      );

      let value = data?.props[field.field] || '';

      if (DataType.dataSourceProjects == field.dataType) {
        value = data?.props?.dataSource?.length ? data.props.dataSource : null;
      }

      if (data?.props) {
        if (field?.fieldType == FieldType.reference) {
          value = !value?.length ? null : value;
        }
      }
      validationErrors = getValidationErrors({
        field,
        data,
        value,
        impliedDataProps,
        ...(treeSetting || {}),
        shouldValidate: shouldValidateField
          ? shouldValidateField
          : shouldValidate
      });

      validationErrors?.errors?.forEach(e => {
        if (e.key) allErrorFields.add(e.key);
      });
    });
  }

  pushResponse?.errors?.forEach(e => {
    if (e.key) allErrorFields.add(e.key);
  });
  pushResponse?.warnings?.forEach(e => {
    if (e.key) allErrorFields.add(e.key);
  });

  fieldErrors?.errors?.forEach(e => {
    if (e.key) allErrorFields.add(e.key);
  });
  fieldErrors?.warnings?.forEach(e => {
    if (e.key) allErrorFields.add(e.key);
  });

  React.useEffect(() => {
    setAllErrorFields(allErrorFields);
  }, [shouldValidate, pushResponse, fieldErrors]);

  const numErrorFields = allErrorFields.size;

  if (validateInProgressMsg) {
    return (
      <Alert className="metadata-validation-alert" type="info" loading>
        {validateInProgressMsg}
      </Alert>
    );
  }

  if (
    (!shouldValidate ||
      shouldValidate == ValidationMode.None ||
      numErrorFields == 0) &&
    !pushResponse?.errors?.length &&
    !pushResponse?.warnings?.length
  ) {
    if (!!mqValidationType && !approvalInProgress) {
      const message =
        mqValidationType == ApprovalType.Declined
          ? ApprovalType.Declined
          : ApprovalType.Approved;
      return (
        <Alert
          className="metadata-validation-alert"
          type={mqValidationType == ApprovalType.Declined ? 'error' : 'success'}
        >
          Item has been {message}.
        </Alert>
      );
    }
    if (
      (pushResponse && pushResponse?.isValidation) ||
      (shouldValidate == ValidationMode.Next && numErrorFields == 0)
    ) {
      return <Success />;
    }
    return null;
  } else
    return (
      <Alert
        className="metadata-validation-alert"
        type={numErrorFields > 0 ? 'error' : 'warning'}
      >
        <b>Incomplete Information! </b>There
        {numErrorFields == 1 ? ' is ' : ' are '}
        <b>{numErrorFields}</b> item
        {numErrorFields == 1 ? '' : 's'} which need
        {numErrorFields == 1 ? 's' : ''} your attention
      </Alert>
    );
};

const Success = () => {
  const [showTemporaryMessage, setShowTemporaryMessage] = React.useState(true);
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowTemporaryMessage(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  if (!showTemporaryMessage) return null;
  return (
    <Alert className="metadata-validation-alert" type={'success'}>
      <b>Validation Successful!</b> There are no items which need your
      attention.
    </Alert>
  );
};
